import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "./InvoiceForm";
import { ClaimPhotos } from "./ClaimPhotos";
import { ClaimLog } from "./ClaimLog";

export const gbp2 = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Math.round((Number(n) || 0) * 100) / 100);

export const CLAIM_STATUSES = ["Draft", "Sent", "Settled", "Disputed", "Withdrawn"];
const NEW_ITEM = { ref: "", title: "", description: "", remedy: "", part_no: "", net: "" };
export const EMPTY_CLAIM = {
  claim_date: new Date().toISOString().slice(0, 10), status: "Draft", agreement_no: "", recipient_name: "", recipient_address: "",
  vehicle_description: "", registration: "", vin: "", summary: "", items: [{ ...NEW_ITEM, ref: "1a" }], vat_rate: 20, deadline_days: 7,
  evidence: "Date-stamped photographs are attached. Date-stamped video footage is also available on request.", photos: [], photo_captions: [], notes: "", correspondence: [],
};

const SECTIONS = [
  ["Claim", [["claim_date", "Date of claim", "date"], ["status", "Status", "select", CLAIM_STATUSES], ["deadline_days", "Days to respond or pay", "number"], ["vat_rate", "VAT rate (%)", "number"]]],
  ["Recipient and vehicle (leave blank to use the linked purchase)", [["recipient_name", "Seller name"], ["recipient_address", "Seller address", "textarea"],
    ["vehicle_description", "Vehicle"], ["registration", "Registration"], ["vin", "VIN"]]],
  ["Wording", [["summary", "Opening paragraph (leave blank for the standard wording)", "textarea"], ["evidence", "Supporting evidence", "textarea"], ["notes", "Internal notes (not printed)", "textarea"]]],
];

const SPAN = { 1: "sm:col-span-1", 2: "sm:col-span-2", 3: "sm:col-span-3", 4: "sm:col-span-4", 6: "sm:col-span-6" };

const ItemRow = ({ item, i, onChange, onRemove }) => (
  <div className="grid gap-3 border border-stone p-4 sm:grid-cols-6" data-testid={`claim-item-${i}`}>
    {[["ref", "Ref", 1], ["title", "Damage", 3], ["net", "Net £", 2], ["description", "Details", 6, "textarea"], ["remedy", "Remedy", 4], ["part_no", "Part no.", 2]].map(([k, l, span, t]) => (
      <div key={k} className={SPAN[span]}>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75" htmlFor={`claim-item-${i}-${k}`}>{l}</label>
        {t === "textarea" ? <textarea id={`claim-item-${i}-${k}`} rows={2} className="field !py-2 text-sm" value={item[k]} onChange={(e) => onChange(k, e.target.value)} data-testid={`claim-item-${i}-${k}`} />
          : <input id={`claim-item-${i}-${k}`} type={k === "net" ? "number" : "text"} step="0.01" className="field !py-2 text-sm" value={item[k]} onChange={(e) => onChange(k, e.target.value)} data-testid={`claim-item-${i}-${k}`} />}
      </div>
    ))}
    <button type="button" onClick={onRemove} className="text-left text-xs font-semibold uppercase tracking-wider text-red-800 sm:col-span-6" data-testid={`claim-item-${i}-remove`}>Remove item</button>
  </div>
);

export function ClaimForm({ initial, claimNo, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [purchases, setPurchases] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setItem = (i) => (k, v) => setF((p) => ({ ...p, items: p.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  useEffect(() => {
    api.get("/admin/purchases").then((r) => setPurchases(Array.isArray(r.data) ? r.data : [])).catch(() => setPurchases([]));
  }, []);
  const net = f.items.reduce((s, it) => s + (Number(it.net) || 0), 0);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.items.length || f.items.some((it) => !it.title.trim())) return toast.error("Each claim item needs a damage description");
    setBusy(true);
    try {
      const body = { ...f, deadline_days: Number(f.deadline_days) || 7, vat_rate: Number(f.vat_rate) || 0, items: f.items.map((it) => ({ ...it, net: Number(it.net) || 0 })) };
      const { data } = claimNo ? await api.put(`/admin/claims/${claimNo}`, body) : await api.post("/admin/claims", body);
      toast.success(`Claim ${data.claim_no} saved`);
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-stone bg-paper p-6" data-testid="claim-form">
      <h2 className="mb-5 font-serif text-2xl">{claimNo ? `Edit claim ${claimNo}` : "New claim"}</h2>
      <label htmlFor="claim-purchase-pick" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Linked purchase (fills in the seller and vehicle details)</label>
      <select id="claim-purchase-pick" className="field mb-6 !py-2.5 text-sm" data-testid="claim-purchase-pick" value={f.agreement_no} onChange={(e) => set("agreement_no")(e.target.value)}>
        <option value="">Not linked</option>
        {purchases.map((p) => <option key={p.agreement_no} value={p.agreement_no}>{`${p.agreement_no} · ${[p.make, p.model].filter(Boolean).join(" ")} · ${p.seller_name}`}</option>)}
      </select>
      {SECTIONS.map(([title, fields]) => (
        <fieldset key={title} className="mb-6">
          <legend className="mb-3 text-sm font-semibold text-forest">{title}</legend>
          <div className="grid gap-4 sm:grid-cols-2">{fields.map(([k, l, t, o]) => <Field key={k} k={k} label={l} type={t} options={o} value={f[k]} onChange={set(k)} prefix="claim" />)}</div>
        </fieldset>
      ))}
      <fieldset className="mb-6">
        <legend className="mb-3 text-sm font-semibold text-forest">Claim items</legend>
        <div className="space-y-3">{f.items.map((it, i) => <ItemRow key={i} item={it} i={i} onChange={setItem(i)} onRemove={() => setF((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }))} />)}</div>
        <button type="button" onClick={() => setF((p) => ({ ...p, items: [...p.items, { ...NEW_ITEM }] }))} className="mt-3 text-xs font-semibold uppercase tracking-wider text-forest" data-testid="claim-item-add">+ Add item</button>
        <p className="mt-3 text-sm" data-testid="claim-total">Net {gbp2(net)} · VAT {gbp2((net * (Number(f.vat_rate) || 0)) / 100)} · <strong>claimed {gbp2(net * (1 + (Number(f.vat_rate) || 0) / 100))}</strong></p>
      </fieldset>
      <ClaimPhotos photos={f.photos} captions={f.photo_captions ?? []}
        onChange={(fn) => setF((p) => { const s = fn({ photos: p.photos, captions: p.photo_captions ?? [] }); return { ...p, photos: s.photos, photo_captions: s.captions }; })} />
      <ClaimLog entries={f.correspondence ?? []} onChange={(list) => setF((p) => ({ ...p, correspondence: list }))} />
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy} className="btn btn-primary !py-3" data-testid="claim-save">{busy ? "Saving…" : claimNo ? "Save changes" : "Save claim"}</button>
        <button type="button" onClick={onCancel} className="btn !py-3" data-testid="claim-cancel">Cancel</button>
      </div>
    </form>
  );
}
